import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArcButton } from "@/components/ArcButton";
import { LabelBadge } from "@/components/LabelBadge";
import { ArrowLeft, Plus, Trash2, Upload, Check, X, Clock, ArrowRight, History, Undo2, Unlink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TITLES = {
  users: "Users", categories: "Categories", images: "Images",
  reports: "Reports & Suggested Edits", settings: "Settings & Site Appearance",
  activity: "Activity Log", achievements: "Achievements", drafts: "Draft Submissions",
  redirects: "Article Redirects", "content-history": "Edit History", "broken-links": "Broken Link Finder",
};

export default function AdminSection({ section }) {
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}><ArrowLeft size={14} /> Dashboard</Link>
      <h1 className="mt-4 font-serif-arc text-3xl font-black">{TITLES[section]}</h1>
      <div className="mt-8">
        {section === "users" && <Users />}
        {section === "categories" && <Categories />}
        {section === "images" && <Images />}
        {section === "reports" && <Reports />}
        {section === "settings" && <SettingsPanel />}
        {section === "activity" && <ActivityLog />}
        {section === "achievements" && <Achievements />}
        {section === "drafts" && <Drafts />}
        {section === "redirects" && <Redirects />}
        {section === "content-history" && <ContentHistory />}
        {section === "broken-links" && <BrokenLinks />}
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-md border p-5 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>{children}</div>;
}

function Users() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  return (
    <div className="overflow-x-auto rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
      <table className="w-full min-w-[500px] text-sm">
        <thead className="border-b text-left" style={{ color: "var(--arc-text-2)" }}>
          <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b last:border-0">
              <td className="px-4 py-3 font-medium">{u.name}</td>
              <td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3"><span className={u.role === "admin" ? "ghoulax-accent font-semibold" : ""}>{u.role}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [enc, setEnc] = useState("ghoulax");
  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/categories", { name, encyclopedia: enc }); setName(""); load(); toast.success("Added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/categories/${id}`); load(); };
  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]"><Label className="mb-1 block text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="w-40"><Label className="mb-1 block text-xs">Encyclopedia</Label>
            <Select value={enc} onValueChange={setEnc}><SelectTrigger style={{ backgroundColor: "var(--arc-bg)" }}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ghoulax">Ghoulax</SelectItem><SelectItem value="zyfley">Zyfley</SelectItem></SelectContent></Select>
          </div>
          <ArcButton variant="ghoulax" onClick={add} data-testid="add-category"><Plus size={16} /> Add</ArcButton>
        </div>
      </Card>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-md border px-4 py-2.5" style={{ backgroundColor: "var(--arc-card)" }}>
            <span><span className={c.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}>●</span> {c.name}</span>
            <button onClick={() => del(c.id)} className="text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Images() {
  const [files, setFiles] = useState([]);
  const load = () => api.get("/files").then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch (err) { toast.error("Upload failed"); }
  };
  const del = async (id) => { await api.delete(`/files/${id}`); load(); };
  return (
    <div className="space-y-6">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold" style={{ backgroundColor: "var(--arc-card)" }} data-testid="images-upload-label">
        <Upload size={16} /> Upload image
        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="images-upload" />
      </label>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {files.map((f) => (
          <div key={f.id} className="group relative overflow-hidden rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
            <img src={fileUrl(f.storage_path)} alt={f.original_filename} className="aspect-square w-full object-cover" />
            <button onClick={() => del(f.id)} className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded bg-red-600 text-white opacity-0 transition-opacity group-hover:opacity-100"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {files.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No images uploaded yet.</p>}
    </div>
  );
}

function Reports() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/contributions").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const review = async (id, status) => { await api.put(`/contributions/${id}`, { status }); toast.success(status === "approved" ? "Approved" : "Rejected"); load(); };
  return (
    <div className="space-y-4">
      {items.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No suggestions submitted yet.</p>}
      {items.map((c) => (
        <div key={c.id} className="rounded-md border p-5 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }} data-testid={`report-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-serif-arc font-bold">{c.article}</h3>
            <span className={`rounded px-2 py-0.5 text-xs font-semibold ${c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
          </div>
          <p className="mt-2 text-sm"><span className="font-semibold">Change:</span> {c.suggested_change}</p>
          {c.source && <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}><span className="font-semibold">Source:</span> {c.source}</p>}
          {c.reason && <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}><span className="font-semibold">Reason:</span> {c.reason}</p>}
          {c.submitter_email && <p className="mt-1 text-xs" style={{ color: "var(--arc-text-2)" }}>From: {c.submitter_email}</p>}
          {c.status === "pending" && (
            <div className="mt-4 flex gap-2">
              <button onClick={() => review(c.id, "approved")} data-testid={`approve-${c.id}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"><Check size={15} /> Approve</button>
              <button onClick={() => review(c.id, "rejected")} data-testid={`reject-${c.id}`} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-semibold text-red-600"><X size={15} /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState({ site_title: "", site_subtitle: "", footer_note: "" });
  useEffect(() => { api.get("/settings").then((r) => setS({ site_title: r.data.site_title || "", site_subtitle: r.data.site_subtitle || "", footer_note: r.data.footer_note || "" })).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => { try { await api.put("/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <Card>
      <div className="space-y-5">
        <div className="space-y-1.5"><Label>Site title</Label><Input value={s.site_title} onChange={(e) => setS({ ...s, site_title: e.target.value })} data-testid="settings-title" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
        <div className="space-y-1.5"><Label>Site subtitle</Label><Input value={s.site_subtitle} onChange={(e) => setS({ ...s, site_subtitle: e.target.value })} data-testid="settings-subtitle" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
        <div className="space-y-1.5"><Label>Footer note</Label><Input value={s.footer_note} onChange={(e) => setS({ ...s, footer_note: e.target.value })} data-testid="settings-footer" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
        <ArcButton variant="ghoulax" onClick={save} data-testid="save-settings">Save Settings</ArcButton>
      </div>
    </Card>
  );
}

function ActivityLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/activity").then((r) => setItems(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
      {items.length === 0 && <p className="p-5 text-sm" style={{ color: "var(--arc-text-2)" }}>No activity recorded yet.</p>}
      {items.map((a) => (
        <div key={a.id} className="flex items-center justify-between border-b px-4 py-3 text-sm last:border-0">
          <span><span className="font-semibold">{a.action}</span> <span style={{ color: "var(--arc-text-2)" }}>{a.detail}</span></span>
          <span className="text-xs" style={{ color: "var(--arc-text-2)" }}>{a.user} · {new Date(a.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function ContentHistory() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/content/history").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const preview = (v) => {
    if (v == null) return "(empty)";
    if (Array.isArray(v)) return `[${v.length} image block${v.length === 1 ? "" : "s"}]`;
    if (typeof v === "object") v = v.text ?? JSON.stringify(v);
    const s = String(v).replace(/<[^>]+>/g, "");
    return s.length > 120 ? s.slice(0, 120) + "…" : s;
  };
  const restore = async (h) => {
    try { await api.put(`/content/${h.key}`, { value: h.value, type: h.type }); toast.success(`Restored ${h.key}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>
        Every inline edit stores its previous version here. Restore any entry with one click to undo an accidental overwrite.
      </p>
      {items.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No edits recorded yet.</p>}
      {items.map((h) => (
        <div key={h.id} data-testid={`history-${h.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <History size={14} style={{ color: "var(--arc-text-2)" }} />
              <span className="font-mono text-xs font-semibold">{h.key}</span>
            </div>
            <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}>Previous value: <span className="italic">{preview(h.value)}</span></p>
            <p className="mt-0.5 text-xs" style={{ color: "var(--arc-text-2)" }}>{h.edited_by} · {new Date(h.created_at).toLocaleString()}</p>
          </div>
          <button onClick={() => restore(h)} data-testid={`restore-${h.id}`} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-semibold"><Undo2 size={15} /> Restore this version</button>
        </div>
      ))}
    </div>
  );
}

function BrokenLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/broken-links").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>
        Internal links and related-article references that point to a slug with no matching article and no redirect. Add a redirect to fix each one.
      </p>
      {loading && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>Scanning…</p>}
      {!loading && items.length === 0 && (
        <div className="flex items-center gap-2 rounded-md border p-5 text-sm" style={{ backgroundColor: "var(--arc-card)" }}>
          <Check size={16} className="text-emerald-600" /> No broken links found. Every internal link resolves.
        </div>
      )}
      {items.map((b) => (
        <div key={`${b.source_slug}-${b.missing_slug}`} data-testid={`broken-${b.source_slug}-${b.missing_slug}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Unlink size={15} className="text-red-600" />
              <Link to={`/article/${b.source_slug}`} className="font-semibold hover:underline">{b.source_title}</Link>
            </div>
            <p className="mt-1 text-sm" style={{ color: "var(--arc-text-2)" }}>
              {b.where} → <span className="font-mono text-red-600">/{b.missing_slug}</span> (missing)
            </p>
          </div>
          <Link to={`/admin/redirects?from=${encodeURIComponent(b.missing_slug)}`} data-testid={`fix-${b.missing_slug}`} className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-semibold" style={{ backgroundColor: "var(--ghoulax)", color: "var(--ghoulax-fg)" }}>
            <ArrowRight size={15} /> Fix with redirect
          </Link>
        </div>
      ))}
    </div>
  );
}

function Redirects() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [articles, setArticles] = useState([]);
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState("");
  const load = () => api.get("/redirects").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const add = async () => {
    if (!from.trim() || !to) { toast.error("Enter a source name and pick a target article"); return; }
    try { await api.post("/redirects", { from_slug: from, to_slug: to }); setFrom(""); setTo(""); load(); toast.success("Redirect created"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/redirects/${id}`); load(); };
  return (
    <div className="space-y-6">
      <Card>
        <p className="mb-4 text-sm" style={{ color: "var(--arc-text-2)" }}>
          Point an old or misspelled article name at the correct entry. Visiting the source name will quietly land readers on the target article.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-end">
          <div><Label className="mb-1 block text-xs">Old / misspelled name</Label><Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="e.g. gholax" data-testid="redirect-from" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="hidden items-center justify-center pb-2 sm:flex"><ArrowRight size={18} style={{ color: "var(--arc-text-2)" }} /></div>
          <div>
            <Label className="mb-1 block text-xs">Target article</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="redirect-to" style={{ backgroundColor: "var(--arc-bg)" }}><SelectValue placeholder="Choose article" /></SelectTrigger>
              <SelectContent>{articles.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.title} (/{a.slug})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ArcButton variant="ghoulax" onClick={add} data-testid="add-redirect"><Plus size={16} /> Add</ArcButton>
        </div>
      </Card>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No redirects yet.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`redirect-row-${r.from_slug}`} className="flex items-center justify-between rounded-md border px-4 py-3 text-sm" style={{ backgroundColor: "var(--arc-card)" }}>
            <span className="flex items-center gap-2"><span className="italic" style={{ color: "var(--arc-text-2)" }}>/{r.from_slug}</span> <ArrowRight size={14} /> <Link to={`/article/${r.to_slug}`} className="font-semibold hover:underline">/{r.to_slug}</Link></span>
            <button onClick={() => del(r.id)} className="text-red-600"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Drafts() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/drafts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const approve = async (slug) => { try { await api.post(`/articles/${slug}/approve`); toast.success("Published"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const reject = async (slug) => { try { await api.post(`/articles/${slug}/reject`); toast.success("Rejected"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div className="space-y-4">
      {items.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No draft submissions awaiting review.</p>}
      {items.map((d) => (
        <div key={d.slug} data-testid={`draft-review-${d.slug}`} className="rounded-md border p-5 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Link to={`/article/${d.slug}`} className="font-serif-arc font-bold hover:underline">{d.title}</Link>
              <span className={`text-xs ${d.encyclopedia === "ghoulax" ? "ghoulax-accent" : "zyfley-accent"}`}>{d.encyclopedia}</span>
            </div>
            <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${d.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}><Clock size={12} /> {d.status}</span>
          </div>
          <p className="mt-2 text-sm" style={{ color: "var(--arc-text-2)" }}>{d.description}</p>
          <p className="mt-1 text-xs" style={{ color: "var(--arc-text-2)" }}>By {d.author_email || "unknown"}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/admin/articles/${d.slug}`} className="rounded border px-3 py-1.5 text-sm font-semibold">Review / Edit</Link>
            {d.status !== "published" && <button onClick={() => approve(d.slug)} data-testid={`approve-draft-${d.slug}`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"><Check size={15} /> Approve &amp; Publish</button>}
            {d.status !== "rejected" && <button onClick={() => reject(d.slug)} data-testid={`reject-draft-${d.slug}`} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm font-semibold text-red-600"><X size={15} /> Reject</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Achievements() {
  const empty = { name: "", description: "", date: "Date unknown", source: "", category: "", encyclopedia: "ghoulax", satire: false };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/achievements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editId) await api.put(`/achievements/${editId}`, form);
      else await api.post("/achievements", form);
      toast.success("Saved"); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const edit = (a) => { setForm({ ...a }); setEditId(a.id); };
  const del = async (id) => { await api.delete(`/achievements/${id}`); load(); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <Card>
        <h3 className="mb-4 font-serif-arc font-bold">{editId ? "Edit achievement" : "New achievement"}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="ach-name" style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={set("description")} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="space-y-1.5"><Label>Date</Label><Input value={form.date} onChange={set("date")} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={set("category")} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="space-y-1.5"><Label>Source</Label><Input value={form.source} onChange={set("source")} style={{ backgroundColor: "var(--arc-bg)" }} /></div>
          <div className="space-y-1.5"><Label>Encyclopedia</Label>
            <Select value={form.encyclopedia} onValueChange={(v) => setForm({ ...form, encyclopedia: v })}><SelectTrigger style={{ backgroundColor: "var(--arc-bg)" }}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="ghoulax">Ghoulax</SelectItem><SelectItem value="zyfley">Zyfley</SelectItem></SelectContent></Select>
          </div>
          <div className="flex items-center gap-2 sm:col-span-2"><Switch checked={form.satire} onCheckedChange={(v) => setForm({ ...form, satire: v })} data-testid="ach-satire" /><Label>Label as SATIRE</Label></div>
        </div>
        <div className="mt-4 flex gap-2">
          <ArcButton variant="ghoulax" onClick={save} data-testid="save-achievement">{editId ? "Update" : "Create"}</ArcButton>
          {editId && <ArcButton variant="outline" onClick={() => { setForm(empty); setEditId(null); }}>Cancel</ArcButton>}
        </div>
      </Card>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-md border px-4 py-3" style={{ backgroundColor: "var(--arc-card)" }}>
            <div className="flex items-center gap-2"><span className="font-semibold">{a.name}</span>{a.satire && <LabelBadge label="SATIRE" />}</div>
            <div className="flex gap-2">
              <button onClick={() => edit(a)} className="rounded border px-2 py-1 text-xs">Edit</button>
              <button onClick={() => del(a.id)} className="text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
